import FetchAndXHRPatcher, {
    CatchedRequest,
} from "@lib/FetchAndXHRPatcher/FetchAndXHRPatcher";
import { Observable, from } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import CartListenerStrategyInterface from "./CartListenerStrategyInterface";
import { CartChangeResponse, RemoveFromCartEvent } from "./types";

class CartRemovalListenerStrategy implements CartListenerStrategyInterface {
    constructor(
        private readonly logger: LoggerInterface,
        private readonly fetchAndXhrPatcher: FetchAndXHRPatcher
    ) {}

    listen(): Observable<RemoveFromCartEvent> {
        return this.fetchAndXhrPatcher.requests$.pipe(
            filter((request: CatchedRequest) =>
                request.url.includes("/cart/change")
            ),
            switchMap((request) =>
                from(request.response).pipe(
                    map((responseData) => ({
                        request,
                        body: responseData.body,
                    }))
                )
            ),
            map(({ body }) => {
                const removedItems = (body as CartChangeResponse)
                    ?.items_removed;

                if (!Array.isArray(removedItems) || removedItems.length === 0) {
                    return null;
                }

                // Array of objects: [{ variant_id, product_id }, ...]
                return removedItems
                    .filter(
                        (
                            item
                        ): item is { variant_id: number; product_id: number } =>
                            typeof item?.variant_id === "number" &&
                            typeof item?.product_id === "number"
                    )
                    .map((item) => ({
                        variantId: item.variant_id,
                        productId: item.product_id,
                    }));
            }),
            filter(
                (
                    items
                ): items is {
                    variantId: number;
                    productId: number;
                }[] => Array.isArray(items) && items.length > 0
            ),
            switchMap((items) =>
                from(items).pipe(
                    map(({ variantId, productId }) => {
                        this.logger.debug(
                            "CartRemovalListenerStrategy: Removed item from cart",
                            { variantId, productId }
                        );
                        return { variantId, productId };
                    })
                )
            )
        );
    }
}

export default CartRemovalListenerStrategy;
